import { useMutation, useQuery } from "@tanstack/react-query";
import { validate } from "uuid";

import InvestmentClient from "@equidefi/portals/clients/InvestmentClient";
import PaymentClient from "@equidefi/portals/clients/PaymentClient";
import { useApi } from "@equidefi/portals/hooks/useApi";
import { useAuth } from "@equidefi/portals/hooks/useAuth";

import { REINVEST_STATUSES } from "@/constants/investment";
import { useEffect, useMemo, useState } from "react";
import StripeClient from "../clients/StripeClient";

/**
 * @param {string|undefined} id
 * @param {import("@tanstack/react-query").QueryObserverOptions} options
 */
export const useInvestment = (id, options = {}) => {
  const investmentApi = useApi(InvestmentClient);

  const validUuid = validate(id);
  const { isLoggedIn } = useAuth();

  return useQuery(["investment", id], () => investmentApi.get(id), {
    enabled: validUuid && isLoggedIn,
    ...options,
  });
};

export const useSetupManualInvestmentPayment = (investmentId, options = {}) => {
  const paymentApi = useApi(PaymentClient);

  return useMutation(
    (data) =>
      paymentApi.setupManualPayment({
        ...data,
        investment_id: investmentId,
      }),
    options,
  );
};

/**
 * @param {Record<"offering_id" | "vault_id", string>} params
 * @param {import("@tanstack/react-query").QueryObserverOptions?} options
 */
export const useUserInvestments = (params, options = {}) => {
  const investmentApi = useApi(InvestmentClient);
  const { isLoggedIn } = useAuth();

  return useQuery(["userInvestments", params], () => investmentApi.all(params), {
    enabled: isLoggedIn,
    ...options,
  });
};

export const useUpdateInvestmentSubscription = (id, options = {}) => {
  const investmentApi = useApi(InvestmentClient);
  return useMutation((data) => investmentApi.updateSubscription(id, data), options);
};

/**
 * @param {string} investmentId
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useInitiatePaymentMutation = (investmentId, options = {}) => {
  const stripeApi = useApi(StripeClient);
  return useMutation(() => stripeApi.createPaymentIntent(investmentId), options);
};

export const useInitiateWirePaymentMutation = (investmentId, options = {}) => {
  const stripeApi = useApi(StripeClient);
  return useMutation(() => stripeApi.createWirePaymentIntent(investmentId), options);
};

export const useAcknowledgeWirePaymentMutation = (investmentId, options = {}) => {
  const stripeApi = useApi(StripeClient);
  return useMutation((paymentIntentId) => stripeApi.acknowledgeWirePayment(investmentId, paymentIntentId), options);
};

const buildAny =
  (investments) =>
  (mapper = () => {}) => {
    return Number(investments?.filter(mapper)?.length) > 0;
  };

export const canReinvestWith = (investments = []) => {
  if (!investments || !investments[0]) return false;

  const any = buildAny(investments);

  const anyPending = any((investment) => investment.status === "PENDING");
  const anyUnderReview = any(
    (investment) => investment.status === "REVIEW" && (investment.next === "DILIGENCE" || investment.expedited),
  );

  if (anyPending) return false;
  if (anyUnderReview) return true;

  return any((investment) => REINVEST_STATUSES.includes(investment.status));
};

export const useCanReinvest = (investments) => {
  return useMemo(() => canReinvestWith(investments), [investments]);
};

const getInvestorFinancialProfile = (investments = []) => {
  const vault = investments.find((investment) => investment?.vault)?.vault;

  if (!vault) return null;

  return {
    isAccredited: vault.accredited,
    annualIncome: Number(vault?.annual_income ?? 0),
    netWorth: Number(vault?.net_worth ?? 0),
  };
};

const sumInvestmentAmounts = (investments = []) => {
  return investments.reduce((sum, investment) => {
    if (investment.status === "CANCELED") return sum;
    return sum + Number(investment?.amount ?? 0);
  }, 0);
};

const getMinimumNextInvestmentAmount = (investments = []) => {
  const prices = investments.map((investment) => Number(investment?.price ?? 0)).filter((price) => price > 0);

  if (!prices.length) return 0;

  return Math.min(...prices);
};

export const passesSuitabilityCheckWith = (investments = []) => {
  if (!Array.isArray(investments) || !investments.length) return false;

  const profile = getInvestorFinancialProfile(investments);
  if (!profile) return false;
  if (profile.isAccredited) return true;

  const limit = Math.max(profile.annualIncome, profile.netWorth) * 0.1;
  const totalInvested = sumInvestmentAmounts(investments);
  const remainingCapacity = limit - totalInvested;

  const minimumNextInvestmentAmount = getMinimumNextInvestmentAmount(investments);

  if (minimumNextInvestmentAmount <= 0) {
    return remainingCapacity > 0;
  }

  return remainingCapacity >= minimumNextInvestmentAmount;
};

export const usePassesSuitabilityCheck = (investments) => {
  return useMemo(() => passesSuitabilityCheckWith(investments), [investments]);
};

/**
 * @param {string} investmentId
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useProcessStripePayment = (investmentId, options = {}) => {
  const stripeApi = useApi(StripeClient);
  return useMutation(
    /**
     * @param {string} paymentId
     */
    (paymentId) => stripeApi.processPayment(investmentId, paymentId),
    options,
  );
};

/**
 * @param {string} investmentId
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useCancelUncompletePayments = (investmentId, options = {}) => {
  const stripeApi = useApi(StripeClient);
  return useMutation(() => stripeApi.cancelUncompleted(investmentId), options);
};

/**
 *
 */
export const useKYCRestricted = (investment, inquiry) => {
  const [isRestricted, setRestricted] = useState(false);

  useEffect(() => {
    if (!investment) return;

    if (investment.vault?.inquiry?.status != null && investment.vault?.inquiry?.status !== "created") {
      setRestricted(true);
    } else if (inquiry?.status != null && inquiry?.status === "created") {
      setRestricted(false);
    } else if (inquiry?.status == null) {
      setRestricted(false);
    } else if (["needs_review", "completed", "pending", "declined"].includes(inquiry?.status)) {
      setRestricted(true);
    } else {
      setRestricted(false);
    }
  }, [investment, inquiry]);

  return isRestricted;
};
